import { keyframes } from "@emotion/react";
import styled from "@emotion/styled";
import { ChronoField, LocalTime } from "@js-joda/core";
import React from "react";
import { STRINGS } from "../strings";
import { minutesToDisplayTimeForDecimalTime } from "../time";
import { translate } from "../translate";
import { HAND_SWIPE } from "../utils/svgs_new";
import { AppIcon } from "./Generic/AppIcon";
import { AppSlider } from "./Generic/AppSlider";
import Flex from "./Generic/Flex";
import { If } from "./Generic/If";

interface Props {
  isDisabled: boolean;
  showHelpAnimation: boolean;
  durationInHours: number;
  value: LocalTime;
  isValidRange: boolean;
  isSelectedTimeInAvailabilityRange: boolean;
  minStartTime: LocalTime;
  maxStartTime: LocalTime;
  onChange?: (value: number | number[] | undefined | null) => void;
}

function AvailableFlexibleTimeRangeSlider(props: Props) {
  const valueMinutes = localTimeToMinutes(props.value);
  const min = localTimeToMinutes(props.minStartTime);
  const max = localTimeToMinutes(props.maxStartTime);

  return (
    <SliderWrapper isDisabled={props.isDisabled}>
      <Flex direction="column" gap={0}>
        <AppSlider
          disabled={props.isDisabled}
          withBars={true}
          startHandler={{
            direction: "start",
            children: minutesToDisplayTimeForDecimalTime(valueMinutes),
          }}
          min={min}
          max={max}
          step={30}
          snapDragDisabled={true}
          value={valueMinutes}
          invalid={!props.isValidRange || !props.isSelectedTimeInAvailabilityRange}
          grayBars={true}
          onChange={props.onChange}
        />
        <If condition={props.showHelpAnimation}>
          <SlidingHandExample />
        </If>
        {!props.isSelectedTimeInAvailabilityRange && !props.showHelpAnimation && (
          <ErrorText>{translate(STRINGS.PATIENT_UNAVAILABLE_DURING_THESE_HOURS)}</ErrorText>
        )}
      </Flex>
    </SliderWrapper>
  );
}

const SliderWrapper = styled.div<{ isDisabled: boolean }>`
  opacity: ${(props) => (props.isDisabled ? 0.2 : 1)};
`;

function localTimeToMinutes(time: LocalTime) {
  return time.get(ChronoField.MINUTE_OF_DAY);
}

function SlidingHandExample() {
  return (
    <SlidingHandAnimation>
      <SlidingHand>
        <AppIcon icon={HAND_SWIPE} style={{ height: 32, color: "#0a77e6" }} />
      </SlidingHand>
      <HintText>Use your finger to choose the visit hours </HintText>
    </SlidingHandAnimation>
  );
}

const HintText = styled.span`
  font-size: 12px;
  color: var(--gray-500);
  text-align: center;
`;

const SlideIn = keyframes`
  from {
    transform: translateX(35%);
  }
  to {
    transform: translateX(85%);
  }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const ErrorText = styled.span`
  font-size: 12px;
  color: var(--red);
  text-align: center;
  animation: ${fadeIn} 0.5s ease-out;
`;

const SlidingHand = styled.div`
  width: 250px;
  animation: 2s linear 0s infinite alternate ${SlideIn};
`;

const SlidingHandAnimation = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

export default AvailableFlexibleTimeRangeSlider;
